/**
 * Serviço para gerenciar permissões do plano no localStorage
 * Diferencia bloqueio por plano vs bloqueio por perfil
 */

import axios from '@axios'

const STORAGE_KEY = 'veltacar_plan_permissions'
const REFRESH_INTERVAL = 30 * 60 * 1000 // 30 minutos em milissegundos

let refreshIntervalId = null

/**
 * Obtém as permissões do plano armazenadas no localStorage
 * @returns {Object|null}
 */
export const getPlanPermissionsFromStorage = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (!stored) return null
        return JSON.parse(stored)
    } catch (error) {
        console.error('Erro ao ler permissões do plano do localStorage:', error)
        return null
    }
}

/**
 * Salva as permissões do plano no localStorage
 * @param {Object} data - Dados das permissões do plano
 */
export const savePlanPermissionsToStorage = (data) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            ...data,
            savedAt: Date.now()
        }))
    } catch (error) {
        console.error('Erro ao salvar permissões do plano no localStorage:', error)
    }
}

/**
 * Remove as permissões do plano do localStorage
 */
export const clearPlanPermissionsFromStorage = () => {
    try {
        localStorage.removeItem(STORAGE_KEY)
    } catch (error) {
        console.error('Erro ao limpar permissões do plano do localStorage:', error)
    }
}

/**
 * Verifica se as permissões armazenadas estão expiradas (mais de 30 minutos)
 * @returns {boolean}
 */
export const arePlanPermissionsExpired = () => {
    const stored = getPlanPermissionsFromStorage()
    if (!stored || !stored.savedAt) return true

    const timeSinceSaved = Date.now() - stored.savedAt
    return timeSinceSaved > REFRESH_INTERVAL
}

/**
 * Busca as permissões do plano do backend
 * @returns {Promise<Object|null>}
 */
export const fetchPlanPermissionsFromBackend = async () => {
    try {
        const response = await axios.get('/api/minhaspermissoes/plano')
        if (response.data) {
            savePlanPermissionsToStorage(response.data)
            return response.data
        }
        return null
    } catch (error) {
        console.error('Erro ao buscar permissões do plano:', error)
        return null
    }
}

/**
 * Obtém as permissões do plano (do cache ou do backend se expirado)
 * @param {boolean} forceRefresh - Força atualização do backend
 * @returns {Promise<Object|null>}
 */
export const getPlanPermissions = async (forceRefresh = false) => {
    if (!forceRefresh && !arePlanPermissionsExpired()) {
        return getPlanPermissionsFromStorage()
    }
    return await fetchPlanPermissionsFromBackend()
}

/**
 * Verifica se o plano atual tem permissão para um recurso específico
 * @param {string} resource - Nome do recurso (ex: 'TAR_IMPORTACAO')
 * @returns {boolean}
 */
export const planHasPermission = (resource) => {
    const stored = getPlanPermissionsFromStorage()
    if (!stored || !stored.permissoes) return true // Em caso de dúvida, não bloqueia

    return stored.permissoes.includes(resource)
}

/**
 * Verifica se o plano atual tem permissão (com validação assíncrona do backend)
 * @param {string} resource - Nome do recurso
 * @returns {Promise<boolean>}
 */
export const planHasPermissionAsync = async (resource) => {
    const data = await getPlanPermissions()
    if (!data || !data.permissoes) return true

    return data.permissoes.includes(resource)
}

/**
 * Verifica se um recurso está bloqueado pelo plano (não pelo perfil)
 * Diferencia: se o plano não tem a permissão = bloqueio de plano
 *             se o plano tem mas o usuário não tem = bloqueio de perfil
 * @param {string} resource - Nome do recurso
 * @returns {boolean}
 */
export const isResourceBlockedByPlanOnly = (resource) => {
    return !planHasPermission(resource)
}

/**
 * Retorna o nome do plano atual
 * @returns {string|null}
 */
export const getCurrentPlanName = () => {
    const stored = getPlanPermissionsFromStorage()
    return stored?.planoNome || null
}

/**
 * Inicia o refresh automático das permissões do plano a cada 30 minutos
 */
export const startAutoRefresh = () => {
    // Limpa intervalo existente se houver
    if (refreshIntervalId) {
        clearInterval(refreshIntervalId)
    }

    // Inicia novo intervalo
    refreshIntervalId = setInterval(() => {
        fetchPlanPermissionsFromBackend()
    }, REFRESH_INTERVAL)
}

/**
 * Para o refresh automático
 */
export const stopAutoRefresh = () => {
    if (refreshIntervalId) {
        clearInterval(refreshIntervalId)
        refreshIntervalId = null
    }
}

/**
 * Inicializa o serviço de permissões do plano
 * Deve ser chamado após o login
 */
export const initPlanPermissions = async () => {
    await fetchPlanPermissionsFromBackend()
    startAutoRefresh()
}

export default {
    getPlanPermissionsFromStorage,
    savePlanPermissionsToStorage,
    clearPlanPermissionsFromStorage,
    arePlanPermissionsExpired,
    fetchPlanPermissionsFromBackend,
    getPlanPermissions,
    planHasPermission,
    planHasPermissionAsync,
    isResourceBlockedByPlanOnly,
    getCurrentPlanName,
    startAutoRefresh,
    stopAutoRefresh,
    initPlanPermissions
}
